<template>
  <tab-navigation :tabs="tabs" :initialTab="route.query.tab || 'contents'" @change-tab="handleTabChange">
    <template #right-content></template>
  </tab-navigation>

  <main>
    <div class="layout-spacing">
      <!-- Eğer seçili içerik varsa ve contents tab'ındaysak, içerik detayını göster -->
      <ContentDetailWrapper v-if="showContentDetail" @content-updated="handleContentUpdated" @back-to-list="handleBackToList"/>
      <!-- Aksi takdirde normal tab içeriğini göster -->
      <component v-else :is="currentTabComponent" :key="currentTab" ref="tabRef"/>
    </div>
  </main>
</template>

<script setup>
import { ref, computed, markRaw, watch, onMounted, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useContentStore } from '@/stores/content'
import TabNavigation from '@/components/base/navigation/TabNavigation.vue'
import contentList from './components/contentList/index.vue'
import newContent from './components/newContent/index.vue'
import videoContent from './components/newVideoContent/index.vue'
import ContentDetailWrapper from './components/ContentDetailWrapper.vue'

// Root component should not inherit attrs
defineOptions({
  inheritAttrs: false,
})

const { t } = useI18n()

const router = useRouter()
const route = useRoute()
const contentStore = useContentStore()

const tabRef = ref(null)
const currentTab = ref(route.query.tab || 'contents')

// Tab tanımlarını burada yaparak daha sonra erişilebilir hale getiriyoruz


// İçerik detayını gösterme durumu
const showContentDetail = computed(() => {
  // Sadece store'da seçili içerik varsa ve contents tab'ındaysak detay görünümünü göster
  return currentTab.value === 'contents' && contentStore.getSelectedContentId !== null
})

// Sayfa yüklenirken kontrol
onMounted(() => {
  // URL parametrelerinden tab'i al, yoksa varsayılan 'contents' kullan
  const tabFromUrl = route.query.tab || 'contents'
  
  // Geçerli tabların listesi (tabs.value kullanmadan)
  const validTabNames = ['contents', 'new-content', 'new-video-content']
  
  // Geçerli tab mi kontrol et
  const isValidTab = validTabNames.includes(tabFromUrl)
  
  // currentTab'i güncelle
  currentTab.value = isValidTab ? tabFromUrl : 'contents'
  
  // URL'i sadece tab parametresi ile güncelle (bir kerelik)
  if (Object.keys(route.query).length > 1 || !route.query.tab || !isValidTab) {
    router.replace({ query: { tab: currentTab.value } })
  }
  
  // Görünüm bilgisini güncelle
  contentStore.lastActiveView = 'list'
})

// İçerik detay sayfasından listeye dönüş
const handleBackToList = async () => {
  // DOM güncellenmesini bekle
  await nextTick()

  // Tab referansı mevcutsa ve fetchData metodu varsa çağır
  if (tabRef.value && typeof tabRef.value.fetchData === 'function') {
    tabRef.value.fetchData()
  }
}

// İçerik güncellendiğinde
const handleContentUpdated = () => {
  // Geçerli seçili içeriği temizle
  contentStore.clearSelectedContent()

  // İçerik güncellendiğinde içerikleri yenile
  if (tabRef.value && typeof tabRef.value.fetchData === 'function') {
    // Önbelleği geçersiz kıl
    contentStore._invalidateContentListCache() 
    // İçerikleri yeniden çek
    tabRef.value.fetchData(true) 
  }
}

// Tab değişimlerini izle
watch(() => currentTab.value, (newTab) => {
  // Tab değiştiyse ve content tab'ı değilse, seçili içeriği temizle
  if (newTab !== 'contents' && contentStore.getSelectedContentId) {
    contentStore.clearSelectedContent()
  }
})

// URL'deki tab parametresini izle
watch(() => route.query.tab, (newTabFromUrl, oldTabFromUrl) => {
  // Şu anda zaten bir tab değişimi işlemindeyse, işlemi işleme
  if (isTabChanging.value) {
    return
  }
  
  // Değişim durumunu işaretle
  isTabChanging.value = true
  
  try {
    if (!newTabFromUrl) {
      // Tab parametresi yoksa varsayılanı kullan
      if (currentTab.value !== 'contents') {
        currentTab.value = 'contents'
      }
      isTabChanging.value = false
      return
    }
  
    // Geçerli tabları kontrol et (sabit değerler kullan)
    const validTabNames = ['contents', 'new-content', 'new-video-content']
    const isValidTab = validTabNames.includes(newTabFromUrl)
    
    if (isValidTab && newTabFromUrl !== currentTab.value) {
      // Geçerli tab ise ve currentTab'den farklıysa güncelle
      currentTab.value = newTabFromUrl
      
      // Tab değiştiğinde içerik görüntüleme modünü kapat
      if (newTabFromUrl !== 'contents') {
        contentStore.clearSelectedContent()
      }
    } else if (!isValidTab) {
      // Geçersiz tab parametresi ise varsayılanı kullan ve URL'i güncelle
      currentTab.value = 'contents'
      // nextTick kullanarak aynı döngüde birden fazla router işlemini engelleyelim
      nextTick(() => {
        router.replace({ query: { tab: 'contents' } }).catch(err => {
          console.error('URL güncelleme hatası:', err)
        })
      })
    }
  } finally {
    // Değişim durumunu kaldır
    setTimeout(() => {
      isTabChanging.value = false
    }, 100) // 100ms sonra değişim kilidini kaldır
  }
})

const tabs = computed(() => [
  { name: 'contents', label: t('entity.tabs.contents'), component: markRaw(contentList) },
  { name: 'new-content', label: t('entity.tabs.new_content'), component: markRaw(newContent) },
  { name: 'new-video-content', label: t('entity.tabs.new_video_content'), component: markRaw(videoContent) },
])

const currentTabComponent = computed(() => {
  const tab = tabs.value.find(tab => tab.name === currentTab.value)
  return tab ? tab.component : null
})

// Tab değişikliklerini izleyen state
const isTabChanging = ref(false)

const handleTabChange = (tabName) => {
  // Tab değişimlerinde bu fonksiyon çağrılır
  
  // Şu anda zaten bir tab değişimi işlemindeyse, işlemi işleme
  if (isTabChanging.value) {
    return
  }
  
  // Değişim durumunu işaretle
  isTabChanging.value = true
  
  try {
    // Gelen tab değerinin geçerliliğini kontrol et
    const validTabNames = ['contents', 'new-content', 'new-video-content']
    if (!validTabNames.includes(tabName)) {
      console.error(`Invalid tab name received: ${tabName}, ignoring.`)
      return
    }
    
    // Tab değiştiyse işlem yap
    if (currentTab.value !== tabName) {
      // Local state'i güncelle
      currentTab.value = tabName
      
      // Tab değiştiğinde içerik görüntüleme modünü kapat
      if (tabName !== 'contents') {
        // Store'dan içerik ID'sini temizle
        contentStore.clearSelectedContent()
      }
      
      // Eğer URL'deki tab değeri farklıysa, URL'i güncelle
      if (route.query.tab !== tabName) {
        // Bu sefer sadece URL güncellendiğinde bildirimi tekrar yapmamak için bildirimi buraya almadık
        router.replace({ query: { tab: tabName } }).catch(err => {
          console.error('Tab navigation error:', err)
        })
      }
    }
  } finally {
    // Her durumda değişim durumunu kaldır
    setTimeout(() => {
      isTabChanging.value = false
    }, 100) // 100ms sonra değişim kilidini kaldır
  }
}
</script>
